import { useState, memo, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useTheme } from "@shared/lib/theme";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import ThemeToggle from "@shared/components/navigation/ThemeToggle";
import PullTabMenu from "@shared/components/navigation/PullTabMenu";
import { THEME, Z_INDEX, ANIMATION } from "@shared/constants/designSystem";
import { useWebsite, useSiteGradients } from "@shared/hooks";

const NavLink = ({ to, children, onClick = () => {}, className = "" }) => (
  <Link
    to={to}
    className={`${THEME.COLORS.TEXT.MUTED} hover:text-gray-900 dark:hover:text-white ${ANIMATION.DURATION.DEFAULT} transition-all border-b-2 border-transparent hover:border-gray-900 dark:hover:border-white px-1 ${className}`}
    onClick={onClick}
  >
    {children}
  </Link>
);

NavLink.propTypes = {
  to: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func,
  className: PropTypes.string,
};

const Header = memo(function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { theme } = useTheme();
  // Get gradients from the hook
  const gradients = useSiteGradients();

  // Refs for focus management and positioning
  const menuButtonRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const headerRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });

  // Get website configuration from context
  const { name, navigation, branding } = useWebsite();

  // Use navigation from config or fallback to default for backwards compatibility
  const navigationLinks = navigation?.main || [
    { to: "/tour", label: "Tour" },
    { to: "/shop", label: "Shop" },
    { to: "/gallery", label: "Gallery" },
    { to: "/discography", label: "Discography" },
    { to: "/awards", label: "Awards" },
    { to: "/press", label: "Press" },
    { to: "/history", label: "History" },
    { to: "/contact", label: "Contact" },
  ];

  const handleMenuToggle = () => {
    if (!isMenuOpen && headerRef.current) {
      // Calculate position when opening menu
      const headerRect = headerRef.current.getBoundingClientRect();
      setMenuPosition({
        top: headerRect.bottom,
        left: headerRect.left + 8, // Account for mx-2 padding
        width: Math.min(368, headerRect.width - 16), // Max 368px (max-w-sm) or header width minus padding
      });
    }
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  // Handle Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isMenuOpen) {
        closeMenu();
        // Return focus to the menu button
        menuButtonRef.current?.focus();
      }
    };

    if (isMenuOpen) {
      document.addEventListener("keydown", handleEscape);
      // Focus the first link in the mobile menu when it opens
      const firstLink = mobileMenuRef.current?.querySelector("a");
      firstLink?.focus();
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isMenuOpen]);

  // Focus trap for mobile menu
  useEffect(() => {
    if (!isMenuOpen || !mobileMenuRef.current) return;

    const focusableElements = mobileMenuRef.current.querySelectorAll(
      'a, button, [tabindex]:not([tabindex="-1"])',
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleTabKey = (e) => {
      if (e.key !== "Tab") return;

      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    document.addEventListener("keydown", handleTabKey);
    return () => {
      document.removeEventListener("keydown", handleTabKey);
    };
  }, [isMenuOpen]);

  // Get logo paths from config or use defaults
  const logoLight =
    branding?.logo?.light ||
    `/images/brand/${name.toLowerCase().replace(/\s+/g, "-")}-logo.svg`;
  const logoDark =
    branding?.logo?.dark ||
    `/images/brand/${name.toLowerCase().replace(/\s+/g, "-")}-logo-dark.svg`;

  // Mobile menu dropdown component (rendered via portal)
  const mobileMenuDropdown = isMenuOpen && (
    <div
      ref={mobileMenuRef}
      className={`fixed min-w-[200px] ${THEME.COLORS.BG.DEFAULT} ${THEME.COLORS.BORDER.DEFAULT} shadow-lg rounded-b-lg overflow-hidden sm:hidden ${Z_INDEX.MODAL}`}
      style={{
        top: `${menuPosition.top}px`,
        left: `${menuPosition.left}px`,
        width: `${menuPosition.width}px`,
      }}
    >
      <div className="py-2">
        {navigationLinks.map((link, index) => (
          <div
            key={link.to}
            className={
              index !== navigationLinks.length - 1
                ? "border-b border-gray-100 dark:border-gray-800"
                : ""
            }
          >
            <NavLink
              to={link.to}
              onClick={closeMenu}
              className="block py-3 px-4 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              {link.label}
            </NavLink>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <header
      ref={headerRef}
      className={`${gradients.layoutHeader} ${THEME.COLORS.TEXT.DEFAULT} relative ${Z_INDEX.OVERLAY}`}
    >
      {/* Main navigation */}
      <nav className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Mobile pull-tab menu on far left */}
          <PullTabMenu
            isOpen={isMenuOpen}
            onToggle={handleMenuToggle}
            menuRef={menuButtonRef}
          />

          {/* Navigation links in center */}
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-center">
            <div className="flex space-x-8">
              {navigationLinks.map((link) => (
                <NavLink key={link.to} to={link.to}>
                  {link.label}
                </NavLink>
              ))}
            </div>
          </div>

          {/* Logo and theme toggle on the right */}
          <div className="flex items-center gap-4">
            <ThemeToggle />
            <Link to="/" className="flex items-center">
              <img
                src={theme === "dark" ? logoDark : logoLight}
                alt={`${name} Logo`}
                className="h-12 w-auto"
              />
            </Link>
          </div>
        </div>
      </nav>

      {/* Render mobile menu via portal to escape stacking context */}
      {typeof document !== "undefined" &&
        mobileMenuDropdown &&
        createPortal(mobileMenuDropdown, document.body)}
    </header>
  );
});

export default Header;
